/**
 * React hook for permission checking
 * 
 * This hook provides utilities for checking user permissions in React components
 * based on the current user's role from the Redux store.
 */

import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { Permission, hasPermission, hasAnyPermission, hasAllPermissions, getRolePermissions } from '@/utils/permissions';

export interface UsePermissionsReturn {
  /**
   * Check if the current user has a specific permission
   */
  can: (permission: Permission) => boolean;
  
  /**
   * Check if the current user has any of the specified permissions
   */
  canAny: (permissions: Permission[]) => boolean;
  
  /**
   * Check if the current user has all of the specified permissions
   */
  canAll: (permissions: Permission[]) => boolean;
  
  /**
   * Get all permissions for the current user's role
   */
  permissions: Permission[];
  
  /**
   * Current user's role
   */
  role: string | null;
  
  /**
   * Whether the user is authenticated
   */
  isAuthenticated: boolean;
}

/**
 * Hook for checking user permissions
 */
export function usePermissions(): UsePermissionsReturn {
  const { user } = useSelector((state: RootState) => state.auth);
  
  const role = user?.role || null;
  const isAuthenticated = !!user;
  
  const can = (permission: Permission): boolean => {
    if (!role) return false;
    return hasPermission(role, permission);
  };
  
  const canAny = (permissions: Permission[]): boolean => {
    if (!role) return false;
    return hasAnyPermission(role, permissions);
  };
  
  const canAll = (permissions: Permission[]): boolean => {
    if (!role) return false;
    return hasAllPermissions(role, permissions);
  };
  
  const permissions = role ? getRolePermissions(role) : [];
  
  return {
    can,
    canAny,
    canAll,
    permissions,
    role,
    isAuthenticated,
  };
}

/**
 * Hook for checking specific permission (convenience hook)
 */
export function usePermission(permission: Permission): boolean {
  const { can } = usePermissions();
  return can(permission);
}

/**
 * Hook for checking multiple permissions
 */
export function usePermissionAny(permissions: Permission[]): boolean {
  const { canAny } = usePermissions();
  return canAny(permissions);
}

/**
 * Hook for checking all permissions
 */
export function usePermissionAll(permissions: Permission[]): boolean {
  const { canAll } = usePermissions();
  return canAll(permissions);
}